import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GlobalStyle, { ChildGlobals } from "@insite/mobius/GlobalStyle";
import * as React from "react";
import { ThemeProvider as SCThemeProvider } from "styled-components";

/* Custom Code - Start */
import useIsFirstRender from "@store/Framework/Common/Hooks/useIsFirstRender";
import isShellTheme from "@store/Framework/Common/Utilities/isShellTheme";
import overrideProps from "@store/Framework/Common/Utilities/overrideProps";
import { themeCollection } from "@store/Framework/ThemeConfiguration";
import Styleguide from "@store/Framework/Types/Styleguide";
import { useSelector } from "react-redux";
/* Custom Code - End */

export interface ExtendedTheme extends BaseTheme {
    [key: string]: any;
}

export interface ThemeProviderProps {
    children?: React.ReactNode;
    /** Determines whether the global styles will be created for the site based on this theme. */
    createGlobalStyle?: boolean;
    /** Determines whether children will be provided text styles based on this theme. */
    createChildGlobals?: boolean;
    theme: ExtendedTheme;
    /** Function to localize UI strings. */
    translate: (text: string) => string;
}

type ThemeProviderPropsCompleted = Omit<ThemeProviderProps, "translate"> &
    Required<Pick<ThemeProviderProps, "translate">>;

/**
 * Exposes a theme object to its child components, and creates global CSS rules based on the theme.
 */
const ThemeProvider: React.FC<ThemeProviderPropsCompleted> = ({
    children,
    createGlobalStyle = false,
    createChildGlobals = true,
    theme = baseTheme,
    translate = (text: string) => text,
}) => {
    theme!.translate = translate;

    /* Custom Code - Start */
    const isFirstRender = useIsFirstRender();
    let areSettingsLoaded = true;
    let activeTheme: Styleguide | undefined;

    if (!isShellTheme(theme)) {
        areSettingsLoaded = useSelector((state: Toolkit.Store.ApplicationState) => {
            const value = state.context?.areSettingsLoaded;
            return typeof value === "undefined" ? true : value;
        });

        activeTheme = useSelector((state: Toolkit.Store.ApplicationState) => {
            const theme = state.context?.settings?.settingsCollection?.extensionWebsiteSettings?.theme;
            return theme ? themeCollection[theme] : undefined;
        });
    }

    const extendTheme = React.useCallback(
        () => overrideProps(theme, activeTheme) as Toolkit.Theme,
        [theme, activeTheme],
    );

    const [extendedTheme, setExtendedTheme] = React.useState(extendTheme());

    React.useEffect(() => {
        if (!isFirstRender) {
            setExtendedTheme(extendTheme());
        }
    }, [theme]);

    React.useEffect(() => {
        if (!isFirstRender && activeTheme?.name !== extendedTheme.name) {
            setExtendedTheme(extendTheme());
        }
    }, [activeTheme]);
    /* Custom Code - End */

    return (
        <SCThemeProvider theme={extendedTheme /* Custom Code */}>
            <>
                {areSettingsLoaded && createGlobalStyle && <GlobalStyle /> /* Custom Code */}
                {createChildGlobals && <ChildGlobals data-id="childGlobals">{children}</ChildGlobals>}
                {!createChildGlobals && children}
            </>
        </SCThemeProvider>
    );
};

export default ThemeProvider;
